import { SearchEngine } from './core/SearchEngine.js';
import { ElementDetector } from './core/ElementDetector.js';
import { EventManager } from './core/EventManager.js';
import { URLManager } from './core/URLManager.js';
import { AnalyticsTracker } from './core/AnalyticsTracker.js';
import { HighlightManager } from './ui/HighlightManager.js';
import { AnimationController } from './ui/AnimationController.js';
import { LoadingStates } from './utils/LoadingStates.js';

class PixlSearch {
  constructor(config = {}) {
    this.config = {
      debounce: 500, // Increased from 300ms to 500ms for better UX with longer queries
      minChars: 2,
      highlightClass: 'pixl-highlight',
      urlParams: true,
      animations: true,
      preserveWebflowInteractions: true, // New option to preserve Webflow interactions
      caseSensitive: false,
      exactMatch: false,
      emptyMessage: 'No results found in this category',
      autoInit: true,
      ...config
    };

    this.initialized = false;
    this.connections = [];
    
    this.detector = new ElementDetector();
    this.searchEngine = new SearchEngine(this.config);
    this.eventManager = new EventManager({ debounce: this.config.debounce });
    this.urlManager = new URLManager({ enabled: this.config.urlParams });
    this.analyticsTracker = new AnalyticsTracker({ enabled: false });
    this.highlightManager = new HighlightManager({ highlightClass: this.config.highlightClass });
    // Disable animations if preserving Webflow interactions
    this.animationController = new AnimationController({ 
      enabled: this.config.animations && !this.config.preserveWebflowInteractions 
    });
    this.loadingStates = new LoadingStates();

    if (this.config.autoInit) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }
  }

  init() {
    if (this.initialized) return;
    
    console.log('🔍 Pixl Search initializing...');
    
    const elements = this.detector.detectAll();
    
    if (elements.forms.size === 0 && elements.lists.size === 0) {
      console.warn('⚠️ No Pixl Search elements found. Make sure you have added the required attributes.');
      return;
    }

    this.connections = this.detector.linkFormsToLists();
    
    this.connections.forEach(connection => {
      this.setupConnection(connection);
      
      if (connection.list.highlightColor) {
        this.animationController.updateHighlightColor(connection.list.highlightColor);
      }
      
      if (connection.list.highlightClass) {
        this.highlightManager.updateHighlightClass(connection.list.highlightClass);
        this.animationController.updateHighlightClass(connection.list.highlightClass);
      }
      
      if (connection.list.analyticsKey) {
        this.analyticsTracker.enable(connection.list.analyticsKey);
        console.log('📊 Pixl Search Analytics enabled for', this.analyticsTracker.domain);
      }
    });

    const initialQuery = this.urlManager.init();
    if (initialQuery) {
      const firstInput = document.querySelector('[pixl-search-field]');
      if (firstInput && this.connections.length > 0) {
        firstInput.value = initialQuery;
        // Search across all lists in the first connection
        this.performSearch(initialQuery, this.connections[0].lists);
      }
    }

    this.eventManager.attachKeyboardShortcuts((query) => {
      this.performSearch(query);
    });

    this.urlManager.attachPopStateListener((query) => {
      const firstInput = document.querySelector('[pixl-search-field]');
      if (firstInput) {
        firstInput.value = query;
        this.performSearch(query);
      }
    });

    this.eventManager.emit('initialized', { connections: this.connections });
    
    this.initialized = true;
    console.log('✅ Pixl Search initialized successfully');
  }

  setupConnection(connection) {
    const { form, lists, fields } = connection;
    
    // Register all searchable elements from all lists
    fields.forEach((fieldElements, fieldName) => {
      fieldElements.forEach(({ element, item, list }) => {
        this.searchEngine.registerSearchableElement(element, fieldName, list);
      });
    });

    // Update input listeners to search across all lists
    form.inputs.forEach(input => {
      this.eventManager.attachInputListener(input, (query) => {
        this.performSearch(query, lists);
      });
    });

    // Update form listener to search across all lists
    this.eventManager.attachFormListener(form.element, (query) => {
      this.performSearch(query, lists);
    });

    // Handle URL params for all lists
    lists.forEach(list => {
      if (list.urlParams) {
        this.urlManager.parseCustomParams(list.element);
      }
    });
  }

  performSearch(query, listsOrList = null) {
    // Handle both single list (backward compatibility) and multiple lists
    const targetLists = Array.isArray(listsOrList) ? listsOrList : 
                       listsOrList ? [listsOrList] : 
                       (this.connections[0] && this.connections[0].lists) || [];
    
    if (targetLists.length === 0) return;

    // Set loading state for all lists
    targetLists.forEach(list => {
      this.loadingStates.setLoading(list.element, true);
    });
    
    this.eventManager.emit('search:start', { query });

    // Search across all registered elements (from all lists)
    const results = this.searchEngine.search(query);
    
    // Handle results for each list
    targetLists.forEach(list => {
      // Check if this list has any matches
      const listHasMatches = results.matches.some(match => {
        const item = match.element.closest('[pixl-search-item]') || 
                     match.element.closest('.w-dyn-item') ||
                     match.element.closest('[role="listitem"]');
        return item && list.element.contains(item);
      });
      
      if (listHasMatches) {
        this.animationController.hideEmptyState(list.element, list.emptyTarget);
      } else if (query && query.length >= this.config.minChars) {
        // Use custom empty state styling if available
        const customStyles = {
          color: list.emptyColor,
          fontSize: list.emptySize,
          padding: list.emptyPadding,
          textAlign: list.emptyAlign,
          fontWeight: list.emptyWeight,
          className: list.emptyClass
        };
        this.animationController.showEmptyState(list.element, list.emptyMessage || this.config.emptyMessage, customStyles, list.emptyTarget);
      } else {
        this.animationController.hideEmptyState(list.element, list.emptyTarget);
      }
    });
    
    // Highlight matches across all lists
    if (results.matches.length > 0) {
      this.highlightManager.highlightMatches(results.matches, query);
    } else {
      this.highlightManager.clearHighlights();
    }

    this.urlManager.updateURL(query);
    
    // Track analytics if enabled
    if (query && query.length >= this.config.minChars) {
      this.analyticsTracker.trackSearch(query, results);
    }
    
    // Clear loading state for all lists
    targetLists.forEach(list => {
      this.loadingStates.setLoading(list.element, false);
    });
    this.eventManager.emit('search:complete', { query, results });
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    
    if (newConfig.debounce !== undefined) {
      this.eventManager.config.debounce = newConfig.debounce;
    }
    
    if (newConfig.highlightClass !== undefined) {
      this.highlightManager.updateHighlightClass(newConfig.highlightClass);
      this.animationController.updateHighlightClass(newConfig.highlightClass);
    }
    
    if (newConfig.highlightColor !== undefined) {
      this.animationController.updateHighlightColor(newConfig.highlightColor);
    }
    
    if (newConfig.animations !== undefined) {
      newConfig.animations ? this.animationController.enable() : this.animationController.disable();
    }
    
    if (newConfig.urlParams !== undefined) {
      newConfig.urlParams ? this.urlManager.enable() : this.urlManager.disable();
    }
  }

  refresh() {
    this.searchEngine.clear();
    this.connections = [];
    this.initialized = false;
    this.init();
  }

  destroy() {
    this.eventManager.removeAllListeners();
    this.searchEngine.clear();
    this.highlightManager.clearAllHighlights();
    this.loadingStates.clearAll();
    this.initialized = false;
    this.connections = [];
  }

  on(eventName, callback) {
    return this.eventManager.on(eventName, callback);
  }

  getShareableURL() {
    return this.urlManager.getShareableURL();
  }
}

if (typeof window !== 'undefined') {
  window.PixlSearch = PixlSearch;
  
  const instance = new PixlSearch();
  window.pixlSearch = instance;
}

export { PixlSearch };